import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { GA_ENABLED, initConsentMode, updateConsent } from "@/lib/analytics";

const STORAGE_KEY = "bayo_consent_v1";
const SIX_MONTHS_MS = 1000 * 60 * 60 * 24 * 30 * 6;

const ConsentContext = createContext({
  status: "pending", // 'pending' | 'granted' | 'denied'
  setStatus: () => {},
  reset: () => {},
});

export function ConsentProvider({ children }) {
  const [status, setStatusState] = useState("pending");

  // On mount: initialise Consent Mode (denied defaults) and read prior choice
  useEffect(() => {
    if (GA_ENABLED) initConsentMode();
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      const obj = JSON.parse(raw);
      if (!obj?.ts || Date.now() - obj.ts > SIX_MONTHS_MS) {
        localStorage.removeItem(STORAGE_KEY);
        return;
      }
      if (obj.status === "granted" || obj.status === "denied") {
        setStatusState(obj.status);
        if (GA_ENABLED) updateConsent(obj.status === "granted");
      }
    } catch {
      /* ignore parse errors */
    }
  }, []);

  const setStatus = useCallback((value) => {
    setStatusState(value);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ status: value, ts: Date.now() }));
    } catch {
      /* ignore quota errors */
    }
    if (GA_ENABLED) updateConsent(value === "granted");
  }, []);

  const reset = useCallback(() => {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      /* ignore */
    }
    setStatusState("pending");
  }, []);

  const value = useMemo(() => ({ status, setStatus, reset }), [status, setStatus, reset]);
  return <ConsentContext.Provider value={value}>{children}</ConsentContext.Provider>;
}

export const useConsent = () => useContext(ConsentContext);
